import { getVisibilityType, visibilityTypes } from '../Constants'
import { statesOptions } from '../../../utils/commonMembersPage'

/**
 * Pure extraction of the legacy `member.jsx` license-rebuild effect (the ~100-line block that
 * rebuilt `licensesArray` from `member.Licenses` + `serviceTypes` + `member.ProductivityOverrides`).
 *
 * Behavior is preserved byte-for-byte: the multiselect option list is built from `serviceTypes`,
 * each license is mapped to a license block with its active visit-type options, visibility
 * overrides and productivity custom-durations merged in, and the resulting array is sorted by
 * `stateLicenseValue`. Returns `null` when there is nothing to rebuild (caller decides whether to
 * clear state) so the caller can preserve the original `if (member?.Licenses?.length > 0) … else`
 * branching exactly.
 */

type VisitTypeShape = {
  VisitTypeId?: string | null
  Name?: string | null
  VisibilityOverride?: string | null
  customDuration?: number | null | undefined
  /**
   * Per-visit-type new-patient-spacing opt-in. Declared here because the member page's throttling
   * control is gated on it (`member.tsx` → `productivityVisitTypes`), and the only thing carrying
   * it that far is the wholesale object spread below plus the untyped `getProductivityList`. A
   * `.map()` that narrows a visit type anywhere in that chain would silently disable the control
   * for every provider, with no compile error — declaring the field keeps at least this hop honest.
   */
  IsNewPatientSpacingEnabled?: boolean
}

type ServiceTypeShape = {
  Name?: string | null
  ServiceTypeId?: string | null
  VisitTypes?: VisitTypeShape[] | null
}

type LicenseVisitTypeShape = {
  VisitTypeId?: string | null
  VisibilityOverride?: string | null
}

type LicenseShape = {
  LicenseId?: string | null
  Number?: string | null
  StateCode?: string | null
  LicensedBy?: string | null
  CreateDateTime?: string | null
  VisitTypes?: LicenseVisitTypeShape[] | null
}

type ProductivityOverrideShape = {
  VisitTypeId?: string | null
  Duration?: number | null
}

type MemberShape = {
  Licenses?: LicenseShape[] | null
  ProductivityOverrides?: ProductivityOverrideShape[] | null
}

type MultiSelectVisitTypeOption = {
  isFirstVisitType: boolean
  visibilityType: unknown
  name: string | null | undefined
  value: string
  serviceValue: string
  ServiceTypeId: string | null | undefined
  VisitTypeId: string | null | undefined
  isSelected: boolean
  isActive?: boolean
  baseVisibilityType?: unknown
  customDuration?: number | null | undefined
  VisitTypes: VisitTypeShape[]
}

export type LicenseBlock = {
  licenseBlockId: string | null | undefined
  licenseVisitTypesOptions: MultiSelectVisitTypeOption[]
  licenseVisitTypesDropdownValue: string
  licenseVisitTypesDropdownValueError: string
  licenseVisitTypesDropdownValueLocalError: boolean
  licenseNumberValue: string | null | undefined
  licenseNumberError: boolean
  licenseNumberLocalError: string
  stateLicenseValue: string | null
  licensedByValue: string | null | undefined
  createdDate: string | null | undefined
}

const buildMultiSelectValue = (serviceTypes: ServiceTypeShape[]): MultiSelectVisitTypeOption[] => {
  const multiSelectValue: MultiSelectVisitTypeOption[] = []
  serviceTypes.forEach((service) => {
    ;(service.VisitTypes ?? []).forEach((visit, index) => {
      const serviceName = service.Name ?? ''
      const serviceValue =
        serviceName.includes('-') ? serviceName.substring(0, serviceName.lastIndexOf('-')) : serviceName
      multiSelectValue.push({
        isFirstVisitType: index === 0,
        visibilityType: visibilityTypes.allPatients,
        name: visit.Name,
        value: `${serviceValue} (${visit.Name})`,
        serviceValue,
        ServiceTypeId: service.ServiceTypeId,
        VisitTypeId: visit.VisitTypeId,
        isSelected: false,
        VisitTypes: [visit],
      })
    })
  })
  multiSelectValue.sort((a, b) => a.value.localeCompare(b.value))
  return multiSelectValue
}

const resolveStateLicenseValue = (stateCode: string | null | undefined): string | null => {
  const stateCodeValue = stateCode ?? ''
  const matched = statesOptions.find((t: string) => t.includes(stateCodeValue))
  if (matched) return matched
  if (stateCodeValue === 'VI') return 'Virgin Islands'
  if (stateCodeValue === 'PR') return 'Puerto Rico'
  return null
}

export const buildLicensesArray = (
  member: MemberShape | null | undefined,
  serviceTypes: ServiceTypeShape[],
): LicenseBlock[] | null => {
  if (!member?.Licenses?.length) return null

  const multiSelectValue = buildMultiSelectValue(serviceTypes)

  const options: LicenseBlock[] = member.Licenses.map((license) => {
    const licenseVisitTypes = license.VisitTypes ?? []
    const activeOptionsList = multiSelectValue
      .filter((multi) => licenseVisitTypes.some((t) => t.VisitTypeId === multi.VisitTypeId))
      .map((t) => {
        let visibilityType: unknown = ''
        let baseVisibilityType: unknown = ''
        licenseVisitTypes.forEach((visitType) => {
          if (t.VisitTypeId === visitType.VisitTypeId) {
            visibilityType = getVisibilityType(visitType.VisibilityOverride ?? '')
          }
          baseVisibilityType =
            visibilityType && typeof visibilityType === 'object' && 'BaseVisibilityType' in visibilityType ?
              visibilityType.BaseVisibilityType
            : undefined
        })
        return {
          ...t,
          isSelected: true,
          isActive: true,
          visibilityType,
          baseVisibilityType,
        }
      })

    const tempLicensesList: MultiSelectVisitTypeOption[] = [...multiSelectValue]
    activeOptionsList.forEach((t) => {
      const indexOfVisitType = tempLicensesList.findIndex((option) => option.VisitTypeId === t.VisitTypeId)
      const existing = tempLicensesList[indexOfVisitType]
      if (!existing) return
      tempLicensesList[indexOfVisitType] = {
        ...existing,
        visibilityType: t.visibilityType,
        isSelected: true,
        isActive: true,
      }
    })
    ;(member.ProductivityOverrides ?? []).forEach((prodItem) => {
      const findIndexProductivity = tempLicensesList.findIndex((option) =>
        option.VisitTypes.some((visitType) => visitType.VisitTypeId === prodItem.VisitTypeId),
      )
      const existing = tempLicensesList[findIndexProductivity]
      if (!existing) return
      tempLicensesList[findIndexProductivity] = {
        ...existing,
        VisitTypes: existing.VisitTypes.map((t) => {
          if (prodItem?.VisitTypeId === t?.VisitTypeId) {
            return { ...t, customDuration: prodItem?.Duration }
          }
          return t
        }),
      }
    })

    return {
      licenseBlockId: license.LicenseId,
      licenseVisitTypesOptions: tempLicensesList,
      licenseVisitTypesDropdownValue: '',
      licenseVisitTypesDropdownValueError: '',
      licenseVisitTypesDropdownValueLocalError: false,
      licenseNumberValue: license.Number,
      licenseNumberError: false,
      licenseNumberLocalError: '',
      stateLicenseValue: resolveStateLicenseValue(license.StateCode),
      licensedByValue: license.LicensedBy,
      createdDate: license.CreateDateTime,
    }
  })

  return options.sort((optionOne, optionTwo) =>
    (optionOne.stateLicenseValue ?? '').localeCompare(optionTwo.stateLicenseValue ?? ''),
  )
}
